// Simple meaning that this version does not support OR logic groups

import * as ObjectTypes from 'customer-data-objects/constants/ObjectTypes';
import * as OperatorTypes from './FilterCRMSearchOperatorTypesType';
import * as Operators from 'customer-data-filters-ui/filterQueryFormat/operator/Operators';
import * as PropertyTypes from 'customer-data-objects/property/PropertyTypes';
import { BACKWARD, FORWARD, isRollingDateDirection } from 'customer-data-filters-ui/filterQueryFormat/rollingDates/RollingDateDirections';
import { ESCAPED_RESERVE_WORDS, RESERVE_WORDS, FQF_TO_CRM_SEARCH, CRM_SEARCH_TO_FQF, FORWARD as CONTACT_SEARCH_DIRECTION_FORWARD, BACKWARD as CONTACT_SEARCH_DIRECTION_BACKWARD, RELATIVE_TIME_DURATION } from './constants';
import { List, Map as ImmutableMap, Seq } from 'immutable';
import { And } from '../../filterQueryFormat/logic/FilterLogicGroup';
import MissingField from 'customer-data-filters-ui/filterQueryFormat/MissingField';
import RollingDateConfig from 'customer-data-filters-ui/filterQueryFormat/rollingDates/RollingDateConfig';
import RollingDatePointConfig from '../../filterQueryFormat/rollingDates/RollingDatePointConfig';
import curry from 'transmute/curry';
import getIn from 'transmute/getIn';
import identity from 'transmute/identity';
import invariant from 'react-utils/invariant';
import isString from 'transmute/isString';
import partial from 'transmute/partial';
import protocol from 'transmute/protocol';
import { DYNAMIC, STATIC } from '../../types/operator/RollingDatePointType';
import { hasDynamicDateFilters, hasXDaysWithExclusiveBounds } from '../../permissions/gates';
import { isRelativeTimeProperty } from '../../utils/isRelativeTimeProperty';
import { formatDurationToMilliseconds, formatDurationToUnit, getUnitForCrmRelativeTimePropertyFilter } from '../../utils/durationUtilities';
export const TypeToOperator = ImmutableMap({
  [OperatorTypes.AFTER_ROLLING_DATE_POINT]: Operators.AfterRollingDatePoint,
  [OperatorTypes.BEFORE_ROLLING_DATE_POINT]: Operators.BeforeRollingDatePoint,
  [OperatorTypes.EQUAL]: Operators.Equal,
  [OperatorTypes.GREATER_OR_EQUAL]: Operators.GreaterOrEqual,
  [OperatorTypes.GREATER]: Operators.Greater,
  [OperatorTypes.GREATER_THAN_X_DAYS]: Operators.GreaterRolling,
  [OperatorTypes.IN_RANGE_ROLLING]: Operators.InRollingDateRange,
  [OperatorTypes.IN_RANGE]: Operators.InRange,
  [OperatorTypes.IN]: Operators.In,
  [OperatorTypes.KNOWN]: Operators.Known,
  [OperatorTypes.LESS_OR_EQUAL]: Operators.LessOrEqual,
  [OperatorTypes.LESS]: Operators.Less,
  [OperatorTypes.LESS_THAN_X_DAYS]: Operators.LessRolling,
  [OperatorTypes.NEAR]: Operators.Near,
  [OperatorTypes.NOT_EQUAL]: Operators.NotEqual,
  [OperatorTypes.NOT_IN]: Operators.NotIn,
  [OperatorTypes.NOT_KNOWN]: Operators.NotKnown,
  [OperatorTypes.UPDATED_BEFORE]: Operators.UpdatedBefore,
  [OperatorTypes.UPDATED_AFTER]: Operators.UpdatedAfter,
  [OperatorTypes.TIME_UNIT_TO_DATE]: Operators.InRollingDateRange,
  [OperatorTypes.STARTS_WITH]: Operators.StartsWith,
  [OperatorTypes.ENDS_WITH]: Operators.EndsWith
});
const getInclusiveForXDays = (type, direction, isUngated) => {
  const useDefaultInclusive = !hasXDaysWithExclusiveBounds(isUngated);
  if (useDefaultInclusive) {
    return {
      lowerBoundInclusive: true,
      upperBoundInclusive: true
    };
  }
  if (type === OperatorTypes.GREATER_THAN_X_DAYS && direction === FORWARD) {
    return {
      lowerBoundInclusive: false,
      upperBoundInclusive: true // ignored, is unbounded in this direction
    };
  }
  if (type === OperatorTypes.GREATER_THAN_X_DAYS && direction === BACKWARD) {
    return {
      lowerBoundInclusive: true,
      // ignored, is unbounded in this direction
      upperBoundInclusive: false
    };
  }
  return {
    lowerBoundInclusive: true,
    upperBoundInclusive: true
  };
};
const defaultConvertTo = curry((type, operator) => {
  const result = {
    operator: type,
    property: operator.field.name
  };
  if (operator.has('highValue')) {
    result.highValue = operator.highValue;
  }
  if (operator.has('value')) {
    if (isRelativeTimeProperty(operator.field)) {
      const unit = getUnitForCrmRelativeTimePropertyFilter(operator.value);
      const valueInUnit = formatDurationToUnit(operator.value, unit);
      result.value = valueInUnit;
      result.timeUnit = unit;
    } else {
      result.value = operator.value;
    }
  }
  if (operator.has('propertySuffix')) {
    result.propertySuffix = operator.propertySuffix;
  }
  if (operator.field.type === PropertyTypes.STRING && [Operators.WildCardEqual, Operators.NotWildCardEqual].includes(operator.constructor)) {
    result.value = operator.value.filter(identity).map((str = '') => {
      let safeValue = str.trim();
      if (RESERVE_WORDS.includes(safeValue)) {
        safeValue = `"${safeValue}"`;
      }
      return safeValue;
    }).join(' OR ');
  }
  if (operator.field.type === PropertyTypes.STRING && [Operators.EqualAny, Operators.NotEqualAny].includes(operator.constructor)) {
    result.propertySuffix = 'raw';
    result.value = operator.value.filter(identity).map((str = '') => {
      let safeValue = str.trim().toLowerCase();
      if (RESERVE_WORDS.includes(safeValue)) {
        safeValue = `"${safeValue}"`;
      }
      return safeValue;
    }).join(' OR ');
  }
  if (operator.field.type === PropertyTypes.BOOLEAN && operator.has('value')) {
    result.value = operator.value ? 'true' : 'false';
  }
  if (![Operators.Known, Operators.NotKnown].includes(operator.constructor) && [PropertyTypes.DATE, PropertyTypes.DATE_TIME].includes(operator.field.type)) {
    if (isRelativeTimeProperty(operator.field)) {
      result.dateTimeFormat = RELATIVE_TIME_DURATION;
    } else {
      result.dateTimeFormat = 'DATE';
    }
  }
  return result;
});
const convertToIn = curry((type, operator) => ({
  operator: type,
  property: operator.field.name,
  values: operator.value.toArray()
}));
const convertToNow = curry((type, operator) => ({
  operator: type,
  property: operator.field.name,
  value: '__hs__NOW'
}));
const convertToXDaysWithDirection = curry((type, operator, isUngated) => {
  return Object.assign({
    operator: type,
    property: operator.field.name,
    value: operator.numberOfDays,
    direction: FQF_TO_CRM_SEARCH[operator.direction]
  }, getInclusiveForXDays(type, operator.direction, isUngated));
});
const convertToInRangeRolling = curry((type, operator) => {
  const {
    direction,
    includeFutureDates,
    useFiscalYear,
    isInclusive,
    timeUnit,
    value
  } = operator.value;
  const operatorType = includeFutureDates ? type : OperatorTypes.TIME_UNIT_TO_DATE;
  const filter = {
    operator: operatorType,
    property: operator.field.name,
    inclusive: isInclusive,
    rollForward: direction === FORWARD,
    timeUnit,
    timeUnitCount: value
  };
  if (operator.rollingOffset) {
    filter.rollingOffset = operator.rollingOffset;
  }
  if (useFiscalYear) {
    filter.rollingOffset = '__hs__FISCAL_YEAR_OFFSET';
    filter.rollingOffsetUnit = 'MONTH';
  }
  return filter;
});
const convertToRollingDatePoint = curry((type, operator) => {
  const {
    direction,
    timeUnit,
    value
  } = operator.value;
  const filter = {
    operator: type,
    property: operator.field.name,
    rollForward: direction === FORWARD,
    timeUnit,
    timeUnitCount: value
  };
  return filter;
});
const convertToDateStaticOrDynamic = curry((type, operator) => {
  const isAfterOperator = type === 'AFTER_DATE_STATIC_OR_DYNAMIC';
  if (operator.type === DYNAMIC) {
    const RealOperator = isAfterOperator ? OperatorTypes.AFTER_ROLLING_DATE_POINT : OperatorTypes.BEFORE_ROLLING_DATE_POINT;
    return convertToRollingDatePoint(RealOperator, operator);
  }
  const RealOperator = isAfterOperator ? OperatorTypes.GREATER : OperatorTypes.LESS;
  return defaultConvertTo(RealOperator, operator);
});
const convertToNear = curry((type, operator) => ({
  distanceUnit: 'MILES',
  distanceUnitCount: 25,
  operator: type,
  property: operator.field.name,
  value: operator.value
}));
const convertToUpdatedBefore = curry((type, operator) => ({
  operator: type,
  property: operator.field.name,
  comparison_property: operator.value
}));
const convertToUpdatedAfter = curry((type, operator) => ({
  operator: type,
  property: operator.field.name,
  comparison_property: operator.value
}));

// DO NOT USE: this is not implemented in CRM Search or Sales Views
const convertToStartsWith = curry((type, operator) => ({
  operator: type,
  property: operator.field.name,
  value: operator.value
}));

// DO NOT USE: this is not implemented in CRM Search or Sales Views
const convertToEndsWith = curry((type, operator) => ({
  operator: type,
  property: operator.field.name,
  value: operator.value
}));
const invalidOperator = operator => {
  invariant(false, 'cannot convert filter operator "%s" to CRM Search filter', operator.name);
};
export const convertToCRMSearch = protocol({
  name: 'convertToCRMSearch',
  args: [protocol.TYPE, isString],
  fallback: invalidOperator
});
convertToCRMSearch.implement(Operators.AssociatedTo, defaultConvertTo(OperatorTypes.EQUAL));
convertToCRMSearch.implement(Operators.Equal, defaultConvertTo(OperatorTypes.EQUAL));
convertToCRMSearch.implement(Operators.GreaterOrEqual, defaultConvertTo(OperatorTypes.GREATER_OR_EQUAL));
convertToCRMSearch.implement(Operators.Greater, defaultConvertTo(OperatorTypes.GREATER));
convertToCRMSearch.implement(Operators.GreaterRolling, convertToXDaysWithDirection(OperatorTypes.GREATER_THAN_X_DAYS));
convertToCRMSearch.implement(Operators.InRange, defaultConvertTo(OperatorTypes.IN_RANGE));
convertToCRMSearch.implement(Operators.In, convertToIn(OperatorTypes.IN));
convertToCRMSearch.implement(Operators.InRollingDateRange, convertToInRangeRolling(OperatorTypes.IN_RANGE_ROLLING));
convertToCRMSearch.implement(Operators.Known, defaultConvertTo(OperatorTypes.KNOWN));
convertToCRMSearch.implement(Operators.Less, defaultConvertTo(OperatorTypes.LESS));
convertToCRMSearch.implement(Operators.LessOrEqual, defaultConvertTo(OperatorTypes.LESS_OR_EQUAL));
convertToCRMSearch.implement(Operators.LessRolling, convertToXDaysWithDirection(OperatorTypes.LESS_THAN_X_DAYS));
convertToCRMSearch.implement(Operators.Near, convertToNear(OperatorTypes.NEAR));
convertToCRMSearch.implement(Operators.NotEqual, defaultConvertTo(OperatorTypes.NOT_EQUAL));
convertToCRMSearch.implement(Operators.NotIn, convertToIn(OperatorTypes.NOT_IN));
convertToCRMSearch.implement(Operators.NotKnown, defaultConvertTo(OperatorTypes.NOT_KNOWN));
convertToCRMSearch.implement(Operators.UpdatedBefore, convertToUpdatedBefore(OperatorTypes.UPDATED_BEFORE));
convertToCRMSearch.implement(Operators.UpdatedAfter, convertToUpdatedAfter(OperatorTypes.UPDATED_AFTER));
convertToCRMSearch.implement(Operators.EqualAny, defaultConvertTo(OperatorTypes.EQUAL));
convertToCRMSearch.implement(Operators.NotEqualAny, defaultConvertTo(OperatorTypes.NOT_EQUAL));
convertToCRMSearch.implement(Operators.WildCardEqual, defaultConvertTo(OperatorTypes.EQUAL));
convertToCRMSearch.implement(Operators.NotWildCardEqual, defaultConvertTo(OperatorTypes.NOT_EQUAL));
convertToCRMSearch.implement(Operators.BeforeNow, convertToNow(OperatorTypes.LESS_OR_EQUAL));
convertToCRMSearch.implement(Operators.AfterDateStaticOrDynamic, convertToDateStaticOrDynamic('AFTER_DATE_STATIC_OR_DYNAMIC'));
convertToCRMSearch.implement(Operators.BeforeDateStaticOrDynamic, convertToDateStaticOrDynamic('BEFORE_DATE_STATIC_OR_DYNAMIC'));
convertToCRMSearch.implement(Operators.AfterRollingDatePoint, convertToRollingDatePoint(OperatorTypes.AFTER_ROLLING_DATE_POINT));
convertToCRMSearch.implement(Operators.BeforeRollingDatePoint, convertToRollingDatePoint(OperatorTypes.BEFORE_ROLLING_DATE_POINT));
// DO NOT USE: this is not implemented in CRM Search or Sales Views

convertToCRMSearch.implement(Operators.StartsWith, convertToStartsWith(OperatorTypes.STARTS_WITH));
// DO NOT USE: this is not implemented in CRM Search or Sales Views
convertToCRMSearch.implement(Operators.EndsWith, convertToEndsWith(OperatorTypes.ENDS_WITH));

/**
 * `null` as a value can cause a whole bunch of issues so
 * lets always convert it over to a string.
 */
function convertNullToString(value) {
  return value === null ? 'null' : value;
}
function convertStringToBool(value) {
  return typeof value === 'string' ? value === 'true' : value;
}
export function toCRMSearchSimple(filter, getIsUngated) {
  return filter.conditions.reduce((acc, op) => {
    acc.push(convertToCRMSearch(op, getIsUngated));
    return acc;
  }, []);
}
function defaultConvertFrom(Operator, field, entry) {
  const {
    propertySuffix,
    value,
    timeUnit
  } = entry;
  let safeValue = field.type === PropertyTypes.BOOLEAN ? convertStringToBool(value) : convertNullToString(value);
  if (isRelativeTimeProperty(field)) {
    const valueInMilliseconds = formatDurationToMilliseconds(value, timeUnit);
    safeValue = valueInMilliseconds;
  }
  if (field.type === PropertyTypes.STRING && [Operators.Equal, Operators.NotEqual].includes(Operator)) {
    const valuesArray = typeof value === 'string' ? value.split(' OR ') : [`${value}`];
    const values = value ? List(valuesArray.map(convertNullToString)).map(v => {
      return ESCAPED_RESERVE_WORDS.includes(v) ? v.replace(/"/g, '') : v;
    }) : List();
    if (propertySuffix === 'raw') {
      const RealOperator = Operator === Operators.Equal ? Operators.EqualAny : Operators.NotEqualAny;
      return RealOperator.of(field, values);
    }
    const RealOperator = Operator === Operators.Equal ? Operators.WildCardEqual : Operators.NotWildCardEqual;
    return RealOperator.ofUnsafe(field, values);
  }
  if ([PropertyTypes.DATE, PropertyTypes.DATE_TIME].includes(field.type) && [Operators.LessOrEqual].includes(Operator)) {
    if (value === '__hs__NOW') {
      return Operators.BeforeNow.ofUnsafe(field, safeValue);
    }
  }
  if ([Operators.AfterDateStaticOrDynamic, Operators.BeforeDateStaticOrDynamic].includes(Operator)) {
    return Operator.of(field, value, STATIC);
  }
  return Operator.ofUnsafe(field, safeValue);
}

/**
 * A while back I messed up and allowed listMemberships to have multiple values
 * in an Equal/NotEqual operator which just doesn't work. This converts to an
 * IN/NOT_IN which actually works. -Colby
 */
function convertWithOverride(OverrideOperator, Operator, field, operator) {
  const {
    property,
    value
  } = operator;
  switch (property) {
    case 'associations.labeled':
      return Operators.AssociatedTo.of(field, operator.propertySuffix, operator.value);
    case 'listMemberships.listId':
    case 'ilsListMemberships.listId':
      return OverrideOperator.of(field, List(Array.isArray(value) ? value : value.split(',')));
    default:
      return defaultConvertFrom(Operator, field, operator);
  }
}
function convertFromIn(Operator, field, entry) {
  const {
    values
  } = entry;
  const operatorValues = values ? List(values.map(convertNullToString)) : List();
  return Operator.of(field, operatorValues);
}
function convertFromInRange(Operator, field, opts) {
  // Accept legacy field in addition to new one (#343)
  const {
    ['high_value']: highValueLegacy,
    highValue: highValueDefault,
    value
  } = opts;
  const highValue = typeof highValueDefault !== 'undefined' ? highValueDefault : highValueLegacy;
  return Operator.of(field, value, highValue);
}

// When convertFromXDaysWithDirection is called, we might need to adjust the
// value the to match the expected inclusive flags set in getInclusiveForXDays
const maybeAdjustForInclusive = (value, Operator, entry, isUngated) => {
  const requiresInclusiveAdjustment = hasXDaysWithExclusiveBounds(isUngated) && Operator === Operators.GreaterRolling;
  if (requiresInclusiveAdjustment) {
    const forwardCase = entry.direction === CONTACT_SEARCH_DIRECTION_FORWARD && entry.lowerBoundInclusive !== false;
    const backwardCase = entry.direction === CONTACT_SEARCH_DIRECTION_BACKWARD && entry.upperBoundInclusive !== false;
    if (forwardCase || backwardCase) {
      return value - 1;
    }
  }
  const requiresInclusiveInverseAdjustment = !hasXDaysWithExclusiveBounds(isUngated) && Operator === Operators.GreaterRolling;
  if (requiresInclusiveInverseAdjustment) {
    const forwardCase = entry.direction === CONTACT_SEARCH_DIRECTION_FORWARD && entry.lowerBoundInclusive === false;
    const backwardCase = entry.direction === CONTACT_SEARCH_DIRECTION_BACKWARD && entry.upperBoundInclusive === false;
    if (forwardCase || backwardCase) {
      return value + 1;
    }
  }
  return value;
};
const convertFromXDaysWithDirection = (Operator, field, entry, isUngated) => {
  const value = +entry.value;
  // when direction is missing (for legacy filters)
  if (!entry.direction) {
    const numberOfDays = Math.abs(value);
    const direction = value <= 0 ? BACKWARD : FORWARD;
    return Operator.of(field, maybeAdjustForInclusive(numberOfDays, Operator, entry, isUngated), direction);
  }
  // when direction is present (for legacy filters)
  if (isRollingDateDirection(entry.direction)) {
    return Operator.of(field, maybeAdjustForInclusive(value, Operator, entry, isUngated), entry.direction);
  }
  // when direction is present
  const direction = CRM_SEARCH_TO_FQF[entry.direction] || FORWARD;
  return Operator.of(field, maybeAdjustForInclusive(value, Operator, entry, isUngated), direction);
};
const convertFromUpdatedBefore = (Operator, field, {
  comparison_property
}) => {
  return Operator.of(field, comparison_property);
};
const convertFromUpdatedAfter = (Operator, field, {
  comparison_property
}) => {
  return Operator.of(field, comparison_property);
};
function convertFromInRangeRolling(Operator, field, {
  inclusive = false,
  operator,
  rollForward = false,
  rollingOffset = 0,
  timeUnit,
  timeUnitCount
}) {
  const direction = rollForward === true ? FORWARD : BACKWARD;
  const includeFutureDates = operator === OperatorTypes.IN_RANGE_ROLLING;
  const useFiscalYear = rollingOffset === '__hs__FISCAL_YEAR_OFFSET';
  return Operator.of(field, RollingDateConfig({
    direction,
    includeFutureDates,
    useFiscalYear,
    isInclusive: inclusive,
    timeUnit,
    value: timeUnitCount
  }), useFiscalYear ? 0 : rollingOffset);
}
function convertFromDateStaticOrDynamic(Operator, field, entry) {
  const isDynamicOperator = Operator === Operators.AfterRollingDatePoint || Operator === Operators.BeforeRollingDatePoint;

  //redirect to typical conversion if Operator is Greater or Less and not on a date property
  if (!isDynamicOperator && entry.dateTimeFormat !== 'DATE') {
    return defaultConvertFrom(Operator, field, entry);
  }
  const isAfterOperator = Operator === Operators.AfterRollingDatePoint || Operator === Operators.Greater;
  const RealOperator = isAfterOperator ? Operators.AfterDateStaticOrDynamic : Operators.BeforeDateStaticOrDynamic;
  if (isDynamicOperator) {
    const {
      rollForward = false,
      timeUnit,
      timeUnitCount
    } = entry;
    const direction = rollForward === true ? FORWARD : BACKWARD;
    const value = RollingDatePointConfig({
      direction,
      timeUnit,
      value: timeUnitCount
    });
    return RealOperator.of(field, value, DYNAMIC);
  } else {
    return defaultConvertFrom(RealOperator, field, entry);
  }
}
function convertFromRollingDatePoint(Operator, field, {
  rollForward = false,
  timeUnit,
  timeUnitCount
}) {
  const direction = rollForward === true ? FORWARD : BACKWARD;
  return Operator.of(field, RollingDatePointConfig({
    direction,
    timeUnit,
    value: timeUnitCount
  }));
}
export const convertFromCRMSearch = () => ImmutableMap({
  [OperatorTypes.AFTER_ROLLING_DATE_POINT]: convertFromRollingDatePoint,
  [OperatorTypes.BEFORE_ROLLING_DATE_POINT]: convertFromRollingDatePoint,
  [OperatorTypes.EQUAL]: partial(convertWithOverride, Operators.In),
  [OperatorTypes.GREATER]: defaultConvertFrom,
  [OperatorTypes.GREATER_OR_EQUAL]: defaultConvertFrom,
  [OperatorTypes.GREATER_THAN_X_DAYS]: convertFromXDaysWithDirection,
  [OperatorTypes.IN]: convertFromIn,
  [OperatorTypes.IN_RANGE]: convertFromInRange,
  [OperatorTypes.IN_RANGE_ROLLING]: convertFromInRangeRolling,
  [OperatorTypes.KNOWN]: defaultConvertFrom,
  [OperatorTypes.LESS]: defaultConvertFrom,
  [OperatorTypes.LESS_OR_EQUAL]: defaultConvertFrom,
  [OperatorTypes.LESS_THAN_X_DAYS]: convertFromXDaysWithDirection,
  [OperatorTypes.NEAR]: defaultConvertFrom,
  [OperatorTypes.NOT_EQUAL]: partial(convertWithOverride, Operators.NotIn),
  [OperatorTypes.NOT_IN]: convertFromIn,
  [OperatorTypes.NOT_KNOWN]: defaultConvertFrom,
  [OperatorTypes.UPDATED_BEFORE]: convertFromUpdatedBefore,
  [OperatorTypes.UPDATED_AFTER]: convertFromUpdatedAfter,
  [OperatorTypes.TIME_UNIT_TO_DATE]: convertFromInRangeRolling,
  [OperatorTypes.STARTS_WITH]: defaultConvertFrom,
  [OperatorTypes.ENDS_WITH]: defaultConvertFrom
});
export const convertFromSalesViews = getIsUngated => ImmutableMap({
  [OperatorTypes.AFTER_ROLLING_DATE_POINT]: convertFromDateStaticOrDynamic,
  [OperatorTypes.BEFORE_ROLLING_DATE_POINT]: convertFromDateStaticOrDynamic,
  [OperatorTypes.EQUAL]: partial(convertWithOverride, Operators.In),
  [OperatorTypes.GREATER]: hasDynamicDateFilters(getIsUngated) ? convertFromDateStaticOrDynamic : defaultConvertFrom,
  [OperatorTypes.GREATER_OR_EQUAL]: defaultConvertFrom,
  [OperatorTypes.GREATER_THAN_X_DAYS]: convertFromXDaysWithDirection,
  [OperatorTypes.IN]: convertFromIn,
  [OperatorTypes.IN_RANGE]: convertFromInRange,
  [OperatorTypes.IN_RANGE_ROLLING]: convertFromInRangeRolling,
  [OperatorTypes.KNOWN]: defaultConvertFrom,
  [OperatorTypes.LESS]: hasDynamicDateFilters(getIsUngated) ? convertFromDateStaticOrDynamic : defaultConvertFrom,
  [OperatorTypes.LESS_OR_EQUAL]: defaultConvertFrom,
  [OperatorTypes.LESS_THAN_X_DAYS]: convertFromXDaysWithDirection,
  [OperatorTypes.NEAR]: defaultConvertFrom,
  [OperatorTypes.NOT_EQUAL]: partial(convertWithOverride, Operators.NotIn),
  [OperatorTypes.NOT_IN]: convertFromIn,
  [OperatorTypes.NOT_KNOWN]: defaultConvertFrom,
  [OperatorTypes.UPDATED_BEFORE]: convertFromUpdatedBefore,
  [OperatorTypes.UPDATED_AFTER]: convertFromUpdatedAfter,
  [OperatorTypes.TIME_UNIT_TO_DATE]: convertFromInRangeRolling,
  [OperatorTypes.STARTS_WITH]: defaultConvertFrom,
  [OperatorTypes.ENDS_WITH]: defaultConvertFrom
});
export function fromCRMSearchSimple(fields, json, getIsUngated, defaultFilterFamily = ObjectTypes.CONTACT) {
  // HACK: shim for CRM-Issues#955 transition
  const getField = typeof fields === 'function' ? fields : keyPath => getIn(keyPath, fields);
  if (!fields || !json) {
    return And.of();
  }
  const convertEntry = (filterFamily, entry) => {
    const Operator = TypeToOperator.get(entry.operator);
    invariant(Operator !== undefined, 'contact search operator `%s` is not defined in OperatorConversion', entry.operator);
    const converter = convertFromCRMSearch().get(entry.operator);
    invariant(typeof converter === 'function', 'no converter defined from `%s` to `%s` operator', entry.operator, Operator.toString());
    const fieldRecord = getField([filterFamily, entry.property]) || getField([entry.property]) || MissingField({
      name: entry.property,
      // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      type: PropertyTypes[entry.dateTimeFormat] || PropertyTypes.STRING
    });
    return converter(Operator, fieldRecord, entry, getIsUngated);
  };
  const entries = Seq(json).map(entry => convertEntry(entry.filterFamily || defaultFilterFamily, entry));
  // @ts-expect-error ts-migrate(2488) FIXME: Type 'Iterable<unknown, any>' must have a '[Symbol... Remove this comment to see the full error message
  return And.of(...entries).set('filterFamily', defaultFilterFamily);
}